//src/utils/AuthProvider.tsx
import { SignInFormInputs } from 'app/features/auth/login';
import {
  oneMinute,
  oneSecond,
} from 'app/features/conversations/Hooks/useElapsedTimeFromNow';
import api from 'app/services';
import { questionnairesApi } from 'app/services/questionnaires';
import {
  IUser,
  IUserRole,
  useCreateUserMutation,
  useLazyGetMyProfileQuery,
  UserRoleEnum,
} from 'app/services/user';
import { Auth } from 'aws-amplify';
import Button from 'Components/Buttons/Button';
import { errorToast } from 'Components/Notifications';
import OverlayWindow from 'Components/OverlayWindow';
import { useClosePopup, useCreatePopup } from 'Components/Popups';
import TopBar from 'Components/TopBar';
import React, { useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useDispatch } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

type AuthStates =
  | 'authenticated'
  | 'unauthenticated'
  | 'loading'
  | 'change_password_required';

interface UserContextType extends UserRole {
  authentication: AuthStates;
  setAuthentication: React.Dispatch<React.SetStateAction<AuthStates>>;
  userId: number | undefined;
  user?: IUser;
  isClient: boolean;
  isAccountant: boolean;
  isAdmin: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  cognitoUser?: any;
  setCognitoUser: any;
  userRole: UserRole;
  dashboard: string;
  signOut: (saveLocation?: boolean) => void;
  signIn: (form: SignInFormInputs) => Promise<void>;
  resetPassword(password: string): Promise<IUser>;
}

let UserContext = React.createContext<UserContextType>(null!);

const IDLE_TIME_OUT = 20 * oneMinute;
const PROMPT_TIME_OUT = oneMinute;

export interface UserRole {
  isAdmin: boolean;
  isAccountant: boolean;
  isAccountantAdmin: boolean;
  isAdminNotAccountant: boolean;
  isAccountantNotAdmin: boolean;
  isClient: boolean;
  role?: UserRoleEnum;
  roleName: string;
}
export const getUserRole = (userRoles: IUserRole[] = []): UserRole => {
  const roles = userRoles.map((role) => role.name);
  const isAdmin = roles.includes(UserRoleEnum.ADMIN);
  const isAccountant = roles.includes(UserRoleEnum.ACCOUNTANT);
  const isClient = roles.includes(UserRoleEnum.CLIENT);
  const isAccountantAdmin = isAccountant && isAdmin;
  const isAdminNotAccountant = isAdmin && !isAccountant;
  const isAccountantNotAdmin = isAccountant && !isAdmin;

  const role = isAdmin
    ? UserRoleEnum.ADMIN
    : isAccountant
    ? UserRoleEnum.ACCOUNTANT
    : isClient
    ? UserRoleEnum.CLIENT
    : undefined;

  const roleName = isAccountantAdmin
    ? 'Accountant/Admin'
    : isAdmin
    ? 'Admin'
    : isAccountant
    ? 'Accountant'
    : isClient
    ? 'Client'
    : '';

  return {
    isAdmin,
    isAccountant,
    isAccountantAdmin,
    isAdminNotAccountant,
    isAccountantNotAdmin,
    isClient,
    role,
    roleName,
  };
};

function getHomePage(user?: IUser): string {
  if (!user) return '/login';

  return '/dashboard/home';
}

export function GoToHomePage({ from }: { from?: string }) {
  const { user } = useAuth();

  return <Navigate state={{ from }} to={getHomePage(user)} />;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authentication, setAuthentication] =
    React.useState<AuthStates>('loading');
  const [cognitoUser, setCognitoUser] = useState<any>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const goToPreSignInLocation = () => navigate(location.state.from);
  const goHome = (user: IUser) => navigate(getHomePage(user));
  const [getUser, { data: userData }] = useLazyGetMyProfileQuery();

  const [createUser] = useCreateUserMutation();

  let user = userData?.user;
  let userId = user?.id;
  let isAuthenticated = authentication === 'authenticated';

  useEffect(() => {
    Auth.currentSession()
      .then((session) => {
        const idTokenExpire = session.getIdToken().getExpiration();
        const refreshToken = session.getRefreshToken();
        const currentTimeSeconds = Math.round(+new Date() / 1000);
        return Auth.currentAuthenticatedUser().then((user) => {
          idTokenExpire < currentTimeSeconds
            ? user.refreshSession(refreshToken, (err: any, data: any) => {
                err ? signOut(true) : authenticate();
              })
            : authenticate();
        });
      })
      .catch(signOut);
  }, []);

  const authenticate = () =>
    getUser()
      .unwrap()
      .then(({ user }) => {
        setAuthentication('authenticated');
        return user;
      })
      .catch((error) => {
        // If the request failed (e.g., 401/403 from server), sign out
        errorToast(
          'Account Deleted, Please contact us at info@acctsonair.com for more information.',
        );
        signOut(true);
        return Promise.reject(error);
      });

  const signOut = async (saveLocation = false) => {
    try {
      await Auth.signOut();
      setAuthentication('unauthenticated');
      setCognitoUser(undefined);
      dispatch(api.util.resetApiState());
      dispatch(questionnairesApi.util.resetApiState());

      Object.keys(localStorage)
        .filter((x) => x.includes('QueryParams@'))
        .forEach((x) => localStorage.removeItem(x));

      if (location.pathname.includes('dashboard')) {
        navigate('/login', {
          replace: true,
          state: saveLocation
            ? { from: location.pathname + location.search }
            : undefined,
        });
      }
    } catch (error) {
      errorToast((error as Error)?.message);
    }
  };
  const PromptModal = useCreatePopup({
    hooks: {
      onClose: () => {
        IdleTimer.start();
      },
    },
    type: 'modal',
  });

  const IdleTimer = useIdleTimer({
    onIdle: () => {
      PromptModal.close();
      signOut(true);
    },
    timeout: IDLE_TIME_OUT,
    startManually: true,
    promptTimeout: PROMPT_TIME_OUT,
    crossTab: true,
    onPrompt: PromptModal.open,
  });

  useEffect(() => {
    const handle = () => {
      Auth.currentSession()
        .then((user) => {
          if (authentication === 'unauthenticated') {
            authenticate();
          }
        })
        .catch(signOut);
    };
    window.addEventListener('storage', handle);

    return () => window.removeEventListener('storage', handle);
  }, [authentication]);

  useEffect(() => {
    if (authentication === 'authenticated') {
      IdleTimer.start();
    }

    if (authentication === 'unauthenticated') {
      IdleTimer.pause();
    }
  }, [authentication]);

  const findOrCreateUserByToken = (token: string) =>
    createUser({ id_token: token }).unwrap().then(authenticate);

  function signIn({ emailOrPhone, password }: SignInFormInputs) {
    return Auth.signIn({
      username: emailOrPhone.toLowerCase(),
      password,
    })
      .then((authRes) => {
        if (authRes.challengeName === 'NEW_PASSWORD_REQUIRED') {
          setCognitoUser(authRes);

          navigate('/set-new-password');

          setAuthentication('change_password_required');
          return;
        }
        return findOrCreateUserByToken(
          authRes.signInUserSession.idToken.jwtToken,
        ).then(location.state?.from ? goToPreSignInLocation : goHome);
      })
      .catch((error) => {
        errorToast((error as Error)?.message);
        return Promise.reject(error);
      });
  }

  function resetPassword(password: string) {
    return Auth.completeNewPassword(cognitoUser, password).then((authRes) => {
      setCognitoUser(authRes);
      return findOrCreateUserByToken(
        authRes.signInUserSession.idToken.jwtToken,
      );
    });
  }

  let userRole = getUserRole(user?.roles);

  let isLoading = authentication === 'loading';

  let value = {
    isLoading,
    authentication,
    setAuthentication,
    isAuthenticated,
    userId,
    user,
    cognitoUser,
    setCognitoUser,
    signOut,
    resetPassword,
    signIn,
    dashboard: isAuthenticated ? '/dashboard' : '/',
    ...userRole,
    userRole,
  };

  return (
    <UserContext.Provider value={value}>
      {isLoading ? (
        <TopBar />
      ) : (
        <>
          {children}
          <PromptModal.Component>
            <InactiveUserModal
              getRemainingTime={IdleTimer.getRemainingTime}
              onReset={IdleTimer.start}
            />
          </PromptModal.Component>
        </>
      )}
    </UserContext.Provider>
  );
}

function InactiveUserModal({
  onReset,
  getRemainingTime,
}: {
  onReset: () => void;
  getRemainingTime: () => number;
}) {
  const timeInSecs = () => Math.floor(getRemainingTime() / 1000);
  const [count, setCount] = useState(timeInSecs());
  const closePopup = useClosePopup();

  useEffect(() => {
    const timer = setTimeout(() => setCount(timeInSecs()), oneSecond);

    if (getRemainingTime() > PROMPT_TIME_OUT) {
      closePopup();
    }
    return () => clearTimeout(timer);
  }, [count, getRemainingTime, timeInSecs]);

  return (
    <>
      <OverlayWindow
        paddingSize="py-4 px-4"
        title=" You have been inactive for some time"
      >
        <div className="flex justify-end items-center gap-2">
          <div className=" text-gray-700 ">Are you still here?</div>
          <Button
            className="max-w-[8rem]"
            onClick={() => {
              onReset();
              closePopup();
            }}
          >
            Yes{' '}
            <div className="w-[20px] flex justify-end font-bold">{count}</div>
          </Button>
        </div>
      </OverlayWindow>
    </>
  );
}

export function useAuth() {
  return React.useContext(UserContext);
}
